import { LuIcon, Menu } from "@overmap-ai/blocks"
import { fieldDataMapping, normalizeFieldOptions, type RadioField } from "@overmap-ai/forms"
import { use, useCallback, useMemo } from "react"

import { SelectFieldOptionMultiSelectGroup } from "../../../components/MenuMultiSelectGroups/SelectFieldOptionMultiSelectGroup"
import { SelectFieldOptionSelectGroup } from "../../../components/MenuSelectGroups/SelectFieldOptionSelectGroup"
import { modelIconMapping } from "../../../mappings/icons"
import { FilterModifierDropdown, RemoveFilterButton } from "../../components"
import { FilterCellBadge } from "../../components/FilterCellBadge"
import { FilterCellButton } from "../../components/FilterCellButton"
import { FilterCellRoot } from "../../components/FilterCellRoot"
import { FilterRendererContext } from "../../contexts/FilterRendererContext/context"
import type { FilterDefRenderProps } from "../BaseFilterDef"
import type { RadioFieldFilterDef } from "./RadioFieldFilterDef"

interface RadioFieldFilterCellProps extends FilterDefRenderProps<RadioFieldFilterDef> {
	field: RadioField
}

export const RadioFieldFilterCell = (props: RadioFieldFilterCellProps) => {
	const { field, filterDef, filter, filterManager } = props

	const { size } = use(FilterRendererContext)

	const filterValue = filter.getFilterValue()
	const filterModifier = filter.getFilterModifier()

	const normalizedOptions = useMemo(() => normalizeFieldOptions(field.options), [field.options])

	const optionValueLabelMapping = useMemo(() => {
		const mapping: Record<string, string> = {}

		for (const option of normalizedOptions) {
			mapping[option.value] = option.label
		}

		return mapping
	}, [normalizedOptions])

	const handleValuesChange = useCallback(
		(values: string[]) => {
			if (values.length > 0) {
				filter.setFilterValue(values)
			} else {
				filter.setFilterValue([])
			}
		},
		[filter],
	)

	const getFilterValueUi = () => {
		switch (filterModifier) {
			case "equals":
			case "notEquals": {
				const equalsModifier = filterDef.modifiers.equals
				const equalsFilterValue =
					filterValue !== undefined && equalsModifier.isFilterValueValid(filterValue)
						? filterValue
						: undefined

				return (
					<Menu.Root>
						<Menu.ClickTrigger>
							<FilterCellButton className="shrink-1 min-w-0 grow">
								<span className="truncate">
									{equalsFilterValue !== undefined
										? equalsFilterValue
											? optionValueLabelMapping[equalsFilterValue]
											: "empty"
										: "..."}
								</span>
							</FilterCellButton>
						</Menu.ClickTrigger>
						<Menu.Content size={size}>
							<Menu.Scroll>
								<SelectFieldOptionSelectGroup
									required={false}
									selectFieldOptions={field.options}
									value={equalsFilterValue ?? undefined}
									onValueChange={filter.setFilterValue}
								/>
								<Menu.CheckboxItem
									checked={equalsFilterValue === null}
									onCheckedChange={() => filter.setFilterValue(null)}
								>
									empty
									<Menu.CheckboxItemIndicator>
										<LuIcon icon={modelIconMapping.check} />
									</Menu.CheckboxItemIndicator>
								</Menu.CheckboxItem>
							</Menu.Scroll>
						</Menu.Content>
					</Menu.Root>
				)
			}
			case "includes":
			case "excludes": {
				const includesModifier = filterDef.modifiers.includes
				const includesModifierFilterValue =
					filterValue !== undefined && includesModifier.isFilterValueValid(filterValue)
						? filterValue
						: undefined

				const getLabelUi = () => {
					switch ((includesModifierFilterValue ?? []).length) {
						case 0:
							return "..."
						case 1:
							return (
								optionValueLabelMapping[includesModifierFilterValue![0]] ??
								includesModifierFilterValue![0]
							)
						default:
							return `${includesModifierFilterValue!.length} options`
					}
				}

				return (
					<Menu.Root>
						<Menu.ClickTrigger>
							<FilterCellButton className="shrink-1 min-w-0 grow">
								<span className="truncate">{getLabelUi()}</span>
							</FilterCellButton>
						</Menu.ClickTrigger>
						<Menu.Content size={size}>
							<Menu.Scroll>
								<SelectFieldOptionMultiSelectGroup
									selectFieldOptions={field.options}
									values={includesModifierFilterValue ?? []}
									onValuesChange={handleValuesChange}
								/>
							</Menu.Scroll>
						</Menu.Content>
					</Menu.Root>
				)
			}
		}
	}

	return (
		<FilterCellRoot>
			<FilterCellBadge className="shrink-1 min-w-0">
				<LuIcon icon={fieldDataMapping[field.type].icon} />
				<span className="truncate">{field.label}</span>
			</FilterCellBadge>

			<FilterModifierDropdown filterDef={filterDef} filter={filter}>
				<FilterCellButton>
					{filterDef.getFilterModifier(filterModifier).modifier.render(filterValue)}
				</FilterCellButton>
			</FilterModifierDropdown>

			{getFilterValueUi()}

			<RemoveFilterButton filter={filter} filterManager={filterManager} />
		</FilterCellRoot>
	)
}
